import api from 'src/config/api';
import { CollectionResponse } from 'src/interfaces/api/api';
import { getJsonResponse } from 'src/utils/ApiUtils';
import Ticket, {
  CreateTicketOptions,
  TicketClosedReason,
  TicketSearchCriteria,
  TicketState,
} from 'src/interfaces/api/Ticket';
import {
  CreateAppointmentOptions,
  CreateNoteOptions,
  CreateReminderOptions,
  CreateTaskOptions,
  LegacyAppointment,
  LegacyNote,
  LegacyReminder,
  LegacyTask,
} from 'src/interfaces/api/Collaborative';
import useAuth from './useAuth';

const useTicket = () => {
  const { fetch } = useAuth();

  const getTicketStates = async (): Promise<CollectionResponse<TicketState>> => {
    const { endpoint, ...options } = api.ticket.getTicketStates();

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<TicketState>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getTicketState = async (id: number): Promise<TicketState> => {
    const { endpoint, ...options } = api.ticket.getTicketState(id);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<TicketState>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getClosedReasons = async (): Promise<CollectionResponse<TicketClosedReason>> => {
    const { endpoint, ...options } = api.ticket.getClosedReasons();

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<TicketClosedReason>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const addAppointment = async (ticketId: string, params: CreateAppointmentOptions): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.addAppointment(ticketId, params);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getInterventionOrderToken = async (ticketId: string, providerId: string): Promise<string> => {
    const { endpoint, ...options } = api.ticket.getInterventionOrderToken(ticketId, providerId);

    const response = await fetch(endpoint, options);

    try {
      const data = await getJsonResponse<{ token: string }>(response);

      return data.token;
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const removeAppointment = async (ticketId: string, AppointmentId: LegacyAppointment['id']): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.removeAppointment(ticketId, AppointmentId);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const addNote = async (ticketId: string, params: CreateNoteOptions): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.addNote(ticketId, params);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const removeNote = async (ticketId: string, NoteId: LegacyNote['id']): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.removeNote(ticketId, NoteId);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const addTask = async (ticketId: string, params: CreateTaskOptions): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.addTask(ticketId, params);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const removeTask = async (ticketId: string, TaskId: LegacyTask['id']): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.removeTask(ticketId, TaskId);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const addReminder = async (ticketId: string, params: CreateReminderOptions): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.addReminder(ticketId, params);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const removeReminder = async (ticketId: string, ReminderId: LegacyReminder['id']): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.removeReminder(ticketId, ReminderId);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const get = async (id: string): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.get(id);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const getAll = async (siteId?: string): Promise<CollectionResponse<Ticket>> => {
    const { endpoint, ...options } = api.ticket.getAll(siteId);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<CollectionResponse<Ticket>>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const search = async (
    criteria: TicketSearchCriteria,
    page: number,
    limit?: number,
    orderBy = 'createdAt',
    order = 'asc'
  ): Promise<
    CollectionResponse<Ticket> & { totalInterventionsDuration: number; totalInterventionsDurationOnSite: number }
  > => {
    const { endpoint, ...options } = api.ticket.search(criteria, page, limit, orderBy, order);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<
        CollectionResponse<Ticket> & { totalInterventionsDuration: number; totalInterventionsDurationOnSite: number }
      >(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const update = async (ticketId: string, values: { [fieldName: string]: any }): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.update(ticketId, values);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const create = async (ticketOptions: CreateTicketOptions): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.create(ticketOptions);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const close = async (id: string, ticketClosedReasonId: number, comment?: string): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.close(id, ticketClosedReasonId, comment);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const dunning = async (id: string, comment?: string): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.dunning(id, comment);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  const open = async (id: string): Promise<Ticket> => {
    const { endpoint, ...options } = api.ticket.open(id);

    const response = await fetch(endpoint, options);

    try {
      return await getJsonResponse<Ticket>(response);
    } catch (e) {
      throw new Error(e.message);
    }
  };

  return {
    getInterventionOrderToken,
    getTicketStates,
    getClosedReasons,
    getTicketState,
    addAppointment,
    removeAppointment,
    addReminder,
    removeReminder,
    addNote,
    removeNote,
    addTask,
    removeTask,
    getAll,
    search,
    create,
    update,
    get,
    open,
    close,
    dunning,
  };
};

export default useTicket;
